import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import TradingVolumeRewardTiersModal from '@flipster/features/src/earn-page/trading-volume-reward-tiers/modal';
import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import {
  EarnTradingBonus,
  earnTradingBonusesBunja,
  getEarnTradingBonus,
} from '@flipster/features/src/shared/earn/state';
import { getTradePath } from '@flipster/features/src/shared/earn/trade-path';
import { useOverlay } from '@flipster/overlay';
import { Badge, Button, TextButton } from '@flipster/react/design-system';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import {
  PusdtCurrency,
  QuoteCurrencyList,
  Usd1Currency,
  UsdPrecision,
  UsdtCurrency,
} from '@flipster/utils/constants/currency';
import { formatToShortNumber } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

const TradingBonusBoostSection = () => {
  const { earnTradingBonusesAtom } = useBunja(earnTradingBonusesBunja);

  const earnTradingBonuses = useAtomValue(earnTradingBonusesAtom);
  const pusdtTradingBonusDetails = getEarnTradingBonus(PusdtCurrency, earnTradingBonuses);
  const usdtTradingBonusDetails = getEarnTradingBonus(UsdtCurrency, earnTradingBonuses);
  const usd1TradingBonusDetails = getEarnTradingBonus(Usd1Currency, earnTradingBonuses);

  const cards: TradingBonusBoostCardProps[] = [
    {
      tradeCurrency: UsdtCurrency,
      tradingBonusDetails: usdtTradingBonusDetails,
      bonusAprCurrencies: [
        [PusdtCurrency, pusdtTradingBonusDetails],
        [UsdtCurrency, usdtTradingBonusDetails],
      ],
    },
    {
      tradeCurrency: Usd1Currency,
      tradingBonusDetails: usd1TradingBonusDetails,
      bonusAprCurrencies: [[Usd1Currency, usd1TradingBonusDetails]],
    },
  ];

  return (
    <div className={stack({ gap: 8 })}>
      <div className={stack({ gap: { base: 16, tablet: 20 } })}>
        <div className={stack({ gap: 8, align: 'start' })}>
          <Badge type='tint'>Trading Bonus</Badge>
          <h2 className={css({ textStyle: 'Title/M', color: 'content.default.level-4' })}>Boost your APR by trading</h2>
        </div>
        <div
          className={flex({
            overflow: 'auto',
            gap: { base: 8, tablet: 16 },
            mx: { base: -16, tablet: 0 },
            px: { base: 16, tablet: 0 },
          })}
        >
          {cards.map((card, idx) => (
            <TradingBonusBoostCard
              key={idx}
              tradeCurrency={card.tradeCurrency}
              tradingBonusDetails={card.tradingBonusDetails}
              bonusAprCurrencies={card.bonusAprCurrencies}
            />
          ))}
        </div>
      </div>
      <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-1', textAlign: 'right' })}>
        {usdtTradingBonusDetails.tradeBonusPeriod} day trading volume, Updated hourly
      </p>
    </div>
  );
};

interface TradingBonusBoostCardProps {
  tradeCurrency: (typeof QuoteCurrencyList)[number];
  tradingBonusDetails: EarnTradingBonus;
  bonusAprCurrencies: [string, EarnTradingBonus][];
}

const TradingBonusBoostCard = ({
  tradeCurrency,
  tradingBonusDetails,
  bonusAprCurrencies,
}: TradingBonusBoostCardProps) => {
  const router = useRouter();

  return (
    <div
      className={stack({
        bg: 'background.default.level-1',
        rounded: 8,
        flex: 1,
        minW: 280,
        p: { base: 12, tablet: 20 },
        gap: { base: 16, tablet: 20 },
      })}
    >
      <div
        className={stack({
          gap: 10,
          align: 'start',
          textStyle: 'Title/XS/Monospace',
          color: 'content.default.level-4',
        })}
      >
        {tradingBonusDetails.remainingNextTradeVolume.gt(0) ? (
          <div>
            Trade{' '}
            <Formatted.ShortNumber
              value={tradingBonusDetails.nextTierTradeVolume.minus(tradingBonusDetails.tradeVolume)}
              suffix={tradeCurrency}
            />{' '}
            more
          </div>
        ) : (
          <div>You are in the highest balance limit tier</div>
        )}
      </div>
      <div
        className={flex({
          gap: 16,
          direction: { base: 'column', tablet: 'row' },
          h: { base: 80, tablet: 40 },
          '& > *': { flex: { base: 'unset', tablet: '1' } },
        })}
      >
        {bonusAprCurrencies?.map(([currency, currencyDetails]) => (
          <BonusAprCurrency
            key={currency}
            aprCurrency={currency}
            tradeCurrency={tradeCurrency}
            nextLimitTierAprPct={currencyDetails.nextLimitTierAprPct.toString()}
            nextTierBalanceCap={currencyDetails.nextTierBalanceCap.toString()}
          />
        ))}
      </div>
      <div className={stack({ gap: 8, textStyle: 'Text/M/Monospace/Regular' })}>
        <div className={css({ bg: 'background.default.level-3', rounded: 99, h: 8, overflow: 'hidden' })}>
          <div
            className={css({ bg: 'background.default.level-4', h: 8 })}
            style={{ width: `${tradingBonusDetails.tradeBonusProgress.toString()}%` }}
          />
        </div>
        <div className={hstack({ justify: 'space-between' })}>
          <Formatted.Number
            value={tradingBonusDetails.tradeVolume.toString()}
            precision={UsdPrecision}
            css={{ color: 'content.default.level-4' }}
          />
          <Formatted.Number
            value={tradingBonusDetails.nextTierTradeVolume.toString()}
            suffix={tradeCurrency}
            css={{ color: 'content.default.level-3' }}
          />
        </div>
      </div>
      <div className={stack({ gap: 8 })}>
        <Button
          label='Trade'
          css={{ w: '100%' }}
          onClick={() => {
            segment.track(EarnEvent.EarnTabTotalUsdtTradeClick);
            router.push(getTradePath(tradeCurrency));
          }}
        />
        <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-1' })}>
          {tradeCurrency} Perpetual only
        </span>
      </div>
    </div>
  );
};

interface BonusAprCurrencyProps {
  aprCurrency: string;
  tradeCurrency: (typeof QuoteCurrencyList)[number];
  nextLimitTierAprPct: string;
  nextTierBalanceCap: string;
}

const BonusAprCurrency = ({
  aprCurrency,
  tradeCurrency,
  nextLimitTierAprPct,
  nextTierBalanceCap,
}: BonusAprCurrencyProps) => {
  const overlay = useOverlay();
  const router = useRouter();
  return (
    <div className={hstack({ h: 40 })}>
      <CurrencyDisplay
        currency={aprCurrency}
        icon={<CurrencyDisplay.Icon size={32} space={12} />}
        mainLabel={
          <TextButton
            label={
              <span className={css({ textStyle: 'Text/L/Monospace/Strong', color: 'content.accent.info' })}>
                +{nextLimitTierAprPct}% APR / {formatToShortNumber(nextTierBalanceCap)}
              </span>
            }
            onClick={() => {
              segment.track(EarnEvent.EarnTabTotalUsdtDetailsClick, { asset_type: 'usdt_premium' });
              overlay.open(
                <TradingVolumeRewardTiersModal
                  buttonText='Trade more and upgrade'
                  currency={tradeCurrency}
                  onClose={() => {
                    router.push(getTradePath(tradeCurrency));
                  }}
                />,
              );
            }}
          />
        }
      />
    </div>
  );
};

export default TradingBonusBoostSection;
